import { api } from '@/lib/api';

type GateEventAction = 'CLICKED' | 'VIEWED' | 'DISMISSED';

interface QueuedGateEvent {
  capabilityKey: string;
  action: GateEventAction;
  metadata?: Record<string, unknown>;
}

class GateEventBatcher {
  private queue: QueuedGateEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly BATCH_SIZE = 10;
  private readonly FLUSH_INTERVAL = 30_000;

  constructor() {
    // Flush pending events before the user navigates away
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void this.flush();
    });
    window.addEventListener('beforeunload', () => void this.flush());
  }

  track(capabilityKey: string, action: GateEventAction, metadata?: Record<string, unknown>): void {
    this.queue.push({ capabilityKey, action, metadata });
    if (this.queue.length >= this.BATCH_SIZE) {
      void this.flush();
    } else if (!this.timer) {
      this.timer = setTimeout(() => void this.flush(), this.FLUSH_INTERVAL);
    }
  }

  private async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0, this.BATCH_SIZE);

    // If more events remain after the splice, schedule another flush
    if (this.queue.length > 0 && !this.timer) {
      this.timer = setTimeout(() => void this.flush(), this.FLUSH_INTERVAL);
    }

    try {
      await api.post('/vendor/capability-events/batch', { events: batch });
    } catch {
      // Fire and forget — analytics errors must never affect UX
    }
  }
}

export const gateEventBatcher = new GateEventBatcher();
